import { z } from 'zod';
import { CurrencyCode, MinorUnits } from './money';
import { HouseEdgeBps } from './money';

// ---------------------------------------------------------------------------
// Selections and match options
// ---------------------------------------------------------------------------

export const PsrSelection = z.enum(['ROCK', 'PAPER', 'SCISSORS']);
export type PsrSelection = z.infer<typeof PsrSelection>;

export const PsrBestOf = z.union([z.literal(1), z.literal(3), z.literal(5)]);
export type PsrBestOf = z.infer<typeof PsrBestOf>;

// ---------------------------------------------------------------------------
// Game config
// ---------------------------------------------------------------------------

export const PsrGameConfigView = z.object({
  kind: z.literal('PSR'),
  gameCode: z.string(),
  name: z.string(),
  mathVersion: z.string(),
  houseEdgeBps: HouseEdgeBps,
  bestOfOptions: z.array(z.number().int().positive()),
  commitTimeoutSeconds: z.number().int().positive(),
  challengeTtlSeconds: z.number().int().positive(),
});
export type PsrGameConfigView = z.infer<typeof PsrGameConfigView>;

// ---------------------------------------------------------------------------
// Match state machine
// ---------------------------------------------------------------------------

export const PsrMatchState = z.enum([
  'OPEN',
  'COMMITTING',
  'RESOLVED',
  'SETTLING',
  'CLOSED',
  'CANCELLED',
  'VOIDED',
]);
export type PsrMatchState = z.infer<typeof PsrMatchState>;

export const PsrMatchOutcome = z.enum(['CREATOR_WIN', 'CHALLENGER_WIN']);
export type PsrMatchOutcome = z.infer<typeof PsrMatchOutcome>;

export const PsrRoundOutcome = z.enum(['CREATOR_WIN', 'CHALLENGER_WIN', 'TIE']);
export type PsrRoundOutcome = z.infer<typeof PsrRoundOutcome>;

// ---------------------------------------------------------------------------
// Challenge lobby
// ---------------------------------------------------------------------------

export const PsrCreateChallengeRequest = z.object({
  amount: MinorUnits,
  bestOf: PsrBestOf,
  clientEntryId: z.uuid(),
});
export type PsrCreateChallengeRequest = z.infer<typeof PsrCreateChallengeRequest>;

export const PsrChallengeView = z.object({
  challengeId: z.uuid(),
  creatorDisplayName: z.string().nullable(),
  amount: MinorUnits,
  currency: CurrencyCode,
  bestOf: z.number().int(),
  createdAt: z.string(),
  expiresAt: z.string(),
});
export type PsrChallengeView = z.infer<typeof PsrChallengeView>;

export const PsrChallengeListResponse = z.object({
  status: z.literal('OK'),
  challenges: z.array(PsrChallengeView),
});
export type PsrChallengeListResponse = z.infer<typeof PsrChallengeListResponse>;

export const PsrCreateChallengeResponse = z.object({
  status: z.literal('OK'),
  challengeId: z.uuid(),
  matchId: z.uuid(),
  balance: MinorUnits,
});
export type PsrCreateChallengeResponse = z.infer<typeof PsrCreateChallengeResponse>;

export const PsrJoinChallengeRequest = z.object({
  clientEntryId: z.uuid(),
});
export type PsrJoinChallengeRequest = z.infer<typeof PsrJoinChallengeRequest>;

export const PsrJoinChallengeResponse = z.object({
  status: z.literal('OK'),
  matchId: z.uuid(),
  balance: MinorUnits,
});
export type PsrJoinChallengeResponse = z.infer<typeof PsrJoinChallengeResponse>;

export const PsrCancelChallengeResponse = z.object({
  status: z.literal('OK'),
  cancelled: z.boolean(),
});
export type PsrCancelChallengeResponse = z.infer<typeof PsrCancelChallengeResponse>;

// ---------------------------------------------------------------------------
// Commit (sealed escrow)
// ---------------------------------------------------------------------------

export const PsrCommitRequest = z.object({
  commitHash: z.string().regex(/^[0-9a-f]{64}$/),
  clientSecret: z.string().min(1).max(256),
  selection: PsrSelection,
  nonce: z.string().min(1).max(256),
});
export type PsrCommitRequest = z.infer<typeof PsrCommitRequest>;

export const PsrCommitResponse = z.object({
  status: z.literal('OK'),
  matchId: z.uuid(),
  roundNumber: z.number().int(),
  committed: z.boolean(),
  opponentCommitted: z.boolean(),
});
export type PsrCommitResponse = z.infer<typeof PsrCommitResponse>;

// ---------------------------------------------------------------------------
// Match state snapshot (for reconnect and current state)
// ---------------------------------------------------------------------------

export const PsrRoundView = z.object({
  roundNumber: z.number().int(),
  creatorSelection: PsrSelection.nullable(),
  challengerSelection: PsrSelection.nullable(),
  creatorCommitHash: z.string().nullable(),
  challengerCommitHash: z.string().nullable(),
  outcome: PsrRoundOutcome.nullable(),
  creatorRevealed: z.boolean(),
  challengerRevealed: z.boolean(),
});
export type PsrRoundView = z.infer<typeof PsrRoundView>;

export const PsrMatchStateResponse = z.object({
  status: z.literal('OK'),
  matchId: z.uuid(),
  state: PsrMatchState,
  amount: MinorUnits,
  currency: CurrencyCode,
  bestOf: z.number().int(),
  creatorDisplayName: z.string().nullable(),
  challengerDisplayName: z.string().nullable(),
  creatorWins: z.number().int(),
  challengerWins: z.number().int(),
  currentRound: z.number().int(),
  outcome: PsrMatchOutcome.nullable(),
  payout: MinorUnits.nullable(),
  rounds: z.array(PsrRoundView),
  commitDeadline: z.string().nullable(),
  isCreator: z.boolean(),
});
export type PsrMatchStateResponse = z.infer<typeof PsrMatchStateResponse>;

// ---------------------------------------------------------------------------
// Match proof (public verification)
// ---------------------------------------------------------------------------

export const PsrRoundProof = z.object({
  roundNumber: z.number().int(),
  creatorCommitHash: z.string(),
  challengerCommitHash: z.string(),
  creatorClientSecret: z.string(),
  creatorSelection: PsrSelection,
  creatorNonce: z.string(),
  challengerClientSecret: z.string(),
  challengerSelection: PsrSelection,
  challengerNonce: z.string(),
  outcome: PsrRoundOutcome,
});
export type PsrRoundProof = z.infer<typeof PsrRoundProof>;

export const PsrMatchProofResponse = z.object({
  status: z.literal('OK'),
  matchId: z.uuid(),
  scheme: z.literal('psr-client-commit-reveal-v1'),
  bestOf: z.number().int(),
  amount: MinorUnits,
  currency: CurrencyCode,
  outcome: PsrMatchOutcome.nullable(),
  rounds: z.array(PsrRoundProof),
});
export type PsrMatchProofResponse = z.infer<typeof PsrMatchProofResponse>;

// ---------------------------------------------------------------------------
// Player stats
// ---------------------------------------------------------------------------

export const PsrPlayerStatsResponse = z.object({
  status: z.literal('OK'),
  playerId: z.string(),
  matchesPlayed: z.number().int(),
  matchesWon: z.number().int(),
  matchesLost: z.number().int(),
  winRate: z.number(),
  roundsPlayed: z.number().int(),
  rockCount: z.number().int(),
  paperCount: z.number().int(),
  scissorsCount: z.number().int(),
  currentStreak: z.number().int(),
});
export type PsrPlayerStatsResponse = z.infer<typeof PsrPlayerStatsResponse>;

// ---------------------------------------------------------------------------
// WebSocket events
// ---------------------------------------------------------------------------

const PsrWsEventBase = z.object({
  timestamp: z.string(),
});

export const PsrWsChallengeCreated = PsrWsEventBase.extend({
  type: z.literal('challenge_created'),
  challenge: PsrChallengeView,
});

export const PsrWsChallengeRemoved = PsrWsEventBase.extend({
  type: z.literal('challenge_removed'),
  challengeId: z.uuid(),
});

export const PsrWsMatched = PsrWsEventBase.extend({
  type: z.literal('matched'),
  matchId: z.uuid(),
  opponentDisplayName: z.string().nullable(),
  commitDeadline: z.string(),
});

export const PsrWsOpponentCommitted = PsrWsEventBase.extend({
  type: z.literal('opponent_committed'),
  matchId: z.uuid(),
  roundNumber: z.number().int(),
  by: z.enum(['creator', 'challenger']),
});

export const PsrWsRoundResolved = PsrWsEventBase.extend({
  type: z.literal('round_resolved'),
  matchId: z.uuid(),
  roundNumber: z.number().int(),
  creatorSelection: PsrSelection,
  challengerSelection: PsrSelection,
  outcome: PsrRoundOutcome,
  creatorWins: z.number().int(),
  challengerWins: z.number().int(),
  commitDeadline: z.string().nullable(),
});

export const PsrWsMatchResolved = PsrWsEventBase.extend({
  type: z.literal('match_resolved'),
  matchId: z.uuid(),
  outcome: PsrMatchOutcome,
  creatorDisplayName: z.string().nullable(),
  challengerDisplayName: z.string().nullable(),
  amount: MinorUnits,
  currency: CurrencyCode,
  payout: MinorUnits,
});

export const PsrWsTimeout = PsrWsEventBase.extend({
  type: z.literal('timeout'),
  matchId: z.uuid(),
  reason: z.string(),
});

export const PsrWsHeartbeat = z.object({
  type: z.literal('heartbeat'),
});

export type PsrWsServerMessage =
  | z.infer<typeof PsrWsChallengeCreated>
  | z.infer<typeof PsrWsChallengeRemoved>
  | z.infer<typeof PsrWsMatched>
  | z.infer<typeof PsrWsOpponentCommitted>
  | z.infer<typeof PsrWsRoundResolved>
  | z.infer<typeof PsrWsMatchResolved>
  | z.infer<typeof PsrWsTimeout>
  | z.infer<typeof PsrWsHeartbeat>;

// ---------------------------------------------------------------------------
// Round result (for operator reports / player history)
// ---------------------------------------------------------------------------

export const PsrRoundResult = z.object({
  kind: z.literal('PSR'),
  gameCode: z.string(),
  mathVersion: z.string(),
  matchId: z.string(),
  bestOf: z.number().int(),
  outcome: PsrMatchOutcome.nullable(),
  roundsPlayed: z.number().int(),
  scheme: z.literal('psr-client-commit-reveal-v1'),
});
export type PsrRoundResult = z.infer<typeof PsrRoundResult>;
